/**
 * Decryption utilities for Web Viewer
 *
 * Uses XChaCha20-Poly1305 for all decryption.
 * Must match exactly with mobile app's encryption.
 */

import { xchacha20poly1305 } from '@noble/ciphers/chacha';
import { sha256 } from '@noble/hashes/sha256';
import { deriveMetaKey, deriveMetaKeyAssets, deriveMetaKeyAssetDetails } from './keyDerivation';
import { base64ToUint8Array } from '../utils/base64url';

/**
 * Caption data structure (matches mobile app)
 */
export interface CaptionData {
  text: string;
  ts: number;
  date?: string;
  place?: string;
}

/**
 * Asset info from API
 */
export interface AssetInfo {
  id: string;
  kind: 'image' | 'video' | 'audio' | 'thumbnail';
  mime: string;
  width: number | null;
  height: number | null;
  duration_ms: number | null;
  download_url: string;
  thumb_download_url: string | null;
  cek_wrap: string;
  cek_wrap_nonce: string;
  asset_nonce: string;
  nonce_scheme: 'single' | 'segmented';
  segment_size: number | null;
  ct_sha256: string;
  display_order: number;
  /** Optional per-asset detail. Null when the photo has no detail. */
  detail_ct: string | null;
  detail_nonce: string | null;
  /** Set when the detail is a recording rather than text. Not played here. */
  detail_voice_asset_id: string | null;
  detail_voice_duration_ms: number | null;
}

/**
 * Per-asset detail payload (matches mobile app's AssetDetailData)
 */
export interface AssetDetailData {
  text: string;
  ts?: number;
}

/**
 * Decrypt caption using PostKey
 *
 * @param captionCt Base64-encoded ciphertext
 * @param captionNonce Base64-encoded nonce
 * @param postKey 32-byte PostKey
 * @param postId Post ID for AAD
 */
export function decryptCaption(
  captionCt: string,
  captionNonce: string,
  postKey: Uint8Array,
  postId: string
): CaptionData {
  const ciphertext = base64ToUint8Array(captionCt);
  const nonce = base64ToUint8Array(captionNonce);

  if (nonce.length !== 24) {
    throw new Error('Caption nonce must be 24 bytes');
  }

  // Derive MetaKey from PostKey
  const metaKey = deriveMetaKey(postKey);

  // Create AAD (must match mobile app)
  const aad = new TextEncoder().encode(JSON.stringify({ post_id: postId }));

  // Decrypt
  const cipher = xchacha20poly1305(metaKey, nonce, aad);
  const plaintext = cipher.decrypt(ciphertext);

  // Parse JSON
  const decoded = new TextDecoder().decode(plaintext);
  return JSON.parse(decoded) as CaptionData;
}

/**
 * Decrypt a per-asset detail — the text shown under one photo of a post.
 *
 * @param detailCt Base64-encoded ciphertext
 * @param detailNonce Base64-encoded 24-byte nonce
 * @param postKey 32-byte PostKey
 * @param postId Post ID for AAD
 * @param assetId Asset ID for AAD
 */
export function decryptAssetDetail(
  detailCt: string,
  detailNonce: string,
  postKey: Uint8Array,
  postId: string,
  assetId: string
): AssetDetailData {
  const ciphertext = base64ToUint8Array(detailCt);
  const nonce = base64ToUint8Array(detailNonce);

  if (nonce.length !== 24) {
    throw new Error('Asset detail nonce must be 24 bytes');
  }

  const metaKey = deriveMetaKeyAssetDetails(postKey);

  // AAD is built as a LITERAL STRING, not JSON.stringify of an object.
  //
  // The native apps use alphabetically sorted, lowercased keys — asset_id
  // before post_id. Note this is the OPPOSITE order from `unwrapAssetCEK`
  // below, which relies on JS insertion order and puts post_id first. Using
  // JSON.stringify here would silently produce an AAD that decrypts nothing,
  // and only for web users.
  const aad = new TextEncoder().encode(
    `{"asset_id":"${assetId.toLowerCase()}","post_id":"${postId.toLowerCase()}"}`
  );

  const cipher = xchacha20poly1305(metaKey, nonce, aad);
  const plaintext = cipher.decrypt(ciphertext);

  const decoded = new TextDecoder().decode(plaintext);
  return JSON.parse(decoded) as AssetDetailData;
}

/**
 * Unwrap Asset CEK using MetaKey-Assets
 */
export function unwrapAssetCEK(
  cekWrap: string,
  cekWrapNonce: string,
  postKey: Uint8Array,
  postId: string,
  assetId: string
): Uint8Array {
  const ciphertext = base64ToUint8Array(cekWrap);
  const nonce = base64ToUint8Array(cekWrapNonce);

  if (nonce.length !== 24) {
    throw new Error('CEK wrap nonce must be 24 bytes');
  }

  // Derive MetaKey-Assets
  const metaKeyAssets = deriveMetaKeyAssets(postKey);

  // Create AAD (must match mobile app)
  const aad = new TextEncoder().encode(JSON.stringify({
    post_id: postId,
    asset_id: assetId,
  }));

  // Decrypt CEK
  const cipher = xchacha20poly1305(metaKeyAssets, nonce, aad);
  const cek = cipher.decrypt(ciphertext);

  if (cek.length !== 32) {
    throw new Error('Unwrapped CEK has invalid length');
  }

  return new Uint8Array(cek);
}

/**
 * Decrypt asset content (single scheme)
 */
export function decryptAssetSingle(
  ciphertext: Uint8Array,
  assetNonce: string,
  cek: Uint8Array,
  expectedHash?: string
): Uint8Array {
  const nonce = base64ToUint8Array(assetNonce);

  if (nonce.length !== 24) {
    throw new Error('Asset nonce must be 24 bytes');
  }

  // Verify hash if provided
  if (expectedHash) {
    const expectedHashBytes = base64ToUint8Array(expectedHash);
    const actualHash = sha256(ciphertext);

    if (!constantTimeEquals(actualHash, expectedHashBytes)) {
      throw new Error('Asset integrity check failed: SHA-256 mismatch');
    }
  }

  // Decrypt (no AAD for asset content)
  const cipher = xchacha20poly1305(cek, nonce);
  return new Uint8Array(cipher.decrypt(ciphertext));
}

/**
 * Derive nonce for a specific segment
 */
function deriveSegmentNonce(baseNonce: Uint8Array, segmentIndex: number): Uint8Array {
  if (baseNonce.length !== 24) {
    throw new Error('Base nonce must be 24 bytes');
  }

  const segmentNonce = new Uint8Array(baseNonce);

  // Add segment index to last 8 bytes (big-endian)
  const indexBytes = new Uint8Array(8);
  const view = new DataView(indexBytes.buffer);
  view.setBigUint64(0, BigInt(segmentIndex), false);

  // XOR last 8 bytes
  for (let i = 0; i < 8; i++) {
    segmentNonce[16 + i] ^= indexBytes[i];
  }

  return segmentNonce;
}

/**
 * Decrypt asset content (segmented scheme)
 */
export function decryptAssetSegmented(
  ciphertext: Uint8Array,
  assetNonce: string,
  cek: Uint8Array,
  segmentSize: number,
  expectedHash?: string
): Uint8Array {
  const baseNonce = base64ToUint8Array(assetNonce);

  if (baseNonce.length !== 24) {
    throw new Error('Asset base nonce must be 24 bytes');
  }

  // Verify hash if provided
  if (expectedHash) {
    const expectedHashBytes = base64ToUint8Array(expectedHash);
    const actualHash = sha256(ciphertext);

    if (!constantTimeEquals(actualHash, expectedHashBytes)) {
      throw new Error('Asset integrity check failed: SHA-256 mismatch');
    }
  }

  // Each segment has 16 bytes auth tag added
  const authTagSize = 16;
  const encryptedSegmentSize = segmentSize + authTagSize;

  const plaintextSegments: Uint8Array[] = [];
  let offset = 0;
  let segmentIndex = 0;

  while (offset < ciphertext.length) {
    const remainingBytes = ciphertext.length - offset;
    const thisEncryptedSize = Math.min(encryptedSegmentSize, remainingBytes);
    const encryptedSegment = ciphertext.slice(offset, offset + thisEncryptedSize);

    // Derive nonce for this segment
    const segmentNonce = deriveSegmentNonce(baseNonce, segmentIndex);

    // Decrypt segment
    const cipher = xchacha20poly1305(cek, segmentNonce);
    const plaintextSegment = cipher.decrypt(encryptedSegment);
    plaintextSegments.push(new Uint8Array(plaintextSegment));

    offset += thisEncryptedSize;
    segmentIndex++;
  }

  // Concatenate all segments
  const totalLength = plaintextSegments.reduce((sum, seg) => sum + seg.length, 0);
  const plaintext = new Uint8Array(totalLength);
  let writeOffset = 0;
  for (const segment of plaintextSegments) {
    plaintext.set(segment, writeOffset);
    writeOffset += segment.length;
  }

  return plaintext;
}

/**
 * Constant-time comparison to prevent timing attacks
 */
function constantTimeEquals(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) {
    return false;
  }

  let result = 0;
  for (let i = 0; i < a.length; i++) {
    result |= a[i] ^ b[i];
  }

  return result === 0;
}
